import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, CheckCircle2, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "h-11 w-full rounded-lg border border-linen bg-white px-3.5 text-sm outline-none transition-colors focus:border-terra";

const Wholesale = () => {
  const { c } = useContent();
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", interest: "", quantity: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    for (const k of ["name", "email", "phone"]) {
      if (!form[k].trim()) {
        toast.error("Please fill in your name, email and phone");
        return;
      }
    }
    setSending(true);
    try {
      const r = await api.post("/wholesale", form);
      setDone(r.data);
    } catch {
      toast.error("Could not send your enquiry — please try again");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-2xl px-5 pb-24 pt-40 text-center" data-testid="wholesale-success">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage-light">
            <CheckCircle2 className="h-8 w-8 text-sage" />
          </span>
          <h1 className="mt-6 font-serif text-3xl tracking-tight text-espresso">Enquiry received</h1>
          <p className="mt-3 text-sm leading-relaxed text-clay/70">
            Thank you, {done.name.split(" ")[0]}. Your wholesale enquiry <span className="font-mono font-semibold text-espresso">#{done.id}</span> is
            with us — expect a reply on {done.email} within 2 business days.
          </p>
          <Link to="/shop" className="mt-8 inline-block rounded-full bg-espresso px-7 py-3.5 text-sm font-semibold text-cream transition-colors hover:bg-terra">
            Browse the collection
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24 pt-32 sm:px-8" data-testid="wholesale-page">
      <Reveal>
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-terra">{c("wholesale_eyebrow", "Bulk & B2B orders")}</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight text-espresso sm:text-4xl">{c("wholesale_title", "Wholesale enquiries")}</h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-clay/70">
          {c("wholesale_intro", "")}
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-10 rounded-2xl border border-linen bg-white p-6 sm:p-8">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-clay">Your name *</label>
              <input data-testid="wholesale-name" value={form.name} onChange={set("name")} className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-clay">Business / brand</label>
              <input data-testid="wholesale-business" value={form.business} onChange={set("business")} className={inputCls} placeholder="Café, store, hotel…" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-clay">Email *</label>
              <input data-testid="wholesale-email" type="email" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-clay">Phone *</label>
              <input data-testid="wholesale-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-clay">Interested in</label>
              <input data-testid="wholesale-interest" value={form.interest} onChange={set("interest")} className={inputCls} placeholder="Mugs, planters, tableware…" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-clay">Estimated quantity</label>
              <Select value={form.quantity} onValueChange={(v) => setForm((f) => ({ ...f, quantity: v }))}>
                <SelectTrigger data-testid="wholesale-quantity" className="h-11 border-linen bg-white"><SelectValue placeholder="Select range" /></SelectTrigger>
                <SelectContent>
                  {["25–50 pieces", "50–100 pieces", "100–250 pieces", "250+ pieces"].map((q) => (
                    <SelectItem key={q} value={q}>{q}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold text-clay">Tell us more</label>
              <textarea data-testid="wholesale-message" value={form.message} onChange={set("message")} rows={4} className="w-full rounded-lg border border-linen bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-terra" placeholder="Timelines, custom glazes, branding, delivery city…" />
            </div>
          </div>
          <button
            data-testid="wholesale-submit-button"
            onClick={submit}
            disabled={sending}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-terra py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-terra-dark active:scale-[0.98] disabled:opacity-50"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
            {sending ? "Sending…" : "Send wholesale enquiry"}
          </button>
        </div>
      </Reveal>
    </div>
  );
};

export default Wholesale;
